"use client";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import Icon from "@/components/icons/Icons";
import { useIsMobile } from "@/utils/useIsMobile";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { ROOT_URL } from "@/utils/gloableVariables";

interface Tool {
  name: string;
  href: string;
  icon: string;
}

export default function MoreCalcList() {
  const [expanded, setExpanded] = useState(false);

  const isMobile = useIsMobile(769);

  const pathname = usePathname();

  const tools: Tool[] = [
    {
      name: "SIP Calculator",
      href: `/tools-and-calculators/sip-calculator`,
      icon: `sip-calc.png`,
    },
    {
      name: "Step Up SIP Calculator",
      href: `/tools-and-calculators/step-up-sip-calculator`,
      icon: `step-up-calc.png`,
    },
    {
      name: "SWP Calculator",
      href: `/tools-and-calculators/swp-calculator`,
      icon: `swp-calc.png`,
    },
    {
      name: "STP Calculator",
      href: `/tools-and-calculators/stp-calculator`,
      icon: `stp-calc.png`,
    },
    {
      name: "XIRR Calculator",
      href: `/tools-and-calculators/xirr-calculator`,
      icon: `xirr-calc.png?v=4`,
    },
    {
      name: "Gratuity Calculator",
      href: `/tools-and-calculators/gratuity-calculator`,
      icon: `gratuity-calc.png`,
    },
    {
      name: "Rolling Returns",
      href: `/tools-and-calculators/rolling-returns`,
      icon: `rolling-calc.png`,
    },
  ];

  const filteredTools = tools.filter((tool) => {
    const currentPath = pathname.replace(/^\//, "");
    return !currentPath.includes(tool.href.replace(/^\//, ""));
  });

  return (
    <div className="2md:sticky top-22 w-full max-w-400 sm:border-2 border-[#aaa] rounded-xl sm:p-5 sm:shadow-xs bg-white mx-auto">
      <h2 className="text-center font-semibold text-xl sm:text-2xl mb-3 text-primaryBlack">More tools & Calculators</h2>

      <AnimatePresence initial={false}>
        <motion.div
          initial={false}
          animate={{ height: expanded ? "auto" : isMobile ? 140 : 280 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          className="overflow-hidden"
        >
          <ul className="space-y-3 mt-2">
            {filteredTools.map((tool) => (
              <li key={tool.name} className="w-full">
                <Link
                  href={tool.href}
                  className="flex w-full items-center gap-2.5 p-3 border-2 border-[#e4e4e4] rounded-xl hover:bg-gray-50 transition"
                >
                  <Image
                    src={`${ROOT_URL}images/all-calc-tools/${tool.icon}`}
                    width={30}
                    height={30}
                    alt={`Icon for ${tool.name} tool`}
                  />
                  <span className="text-primaryBlack">{tool.name}</span>
                  <Icon.dropdownDownArrow className="text-primaryBlack ml-auto -rotate-90" />
                </Link>
              </li>
            ))}
          </ul>
        </motion.div>
      </AnimatePresence>

      {tools.length > 4 && (
        <button
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-3 group w-full flex items-center justify-center sm:justify-start gap-1 transition-all duration-350 text-sm font-medium text-primaryGreen hover:text-primaryGreen/80"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="25"
            viewBox="0 0 24 25"
            className={`w-5 h-5 transition-all duration-350 ${expanded ? "fill-primaryGreen stroke-white group-hover:fill-primaryGreen/80" : "fill-white stroke-primaryGreen group-hover:fill-primaryGreen/5"}`}
          >
            <path
              d="M12 22.9883C17.5 22.9883 22 18.4883 22 12.9883C22 7.48828 17.5 2.98828 12 2.98828C6.5 2.98828 2 7.48828 2 12.9883C2 18.4883 6.5 22.9883 12 22.9883Z"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M8 12.9883H16" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path
              d="M12 16.9883V8.98828"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-transform duration-350 ${expanded ? "rotate-90 origin-center" : "rotate-0 origin-center"}`}
            />
          </svg>

          {expanded ? "Show Less Calculators" : "Show More Calculators"}
        </button>
      )}
    </div>
  );
}
